/**
 * @file Reads a `projectsByStage` answer into the Projects tab's rows, in
 * server order, with the uncapped project count and `totalValue` (null while
 * refused) and the value column's refusal. Consumer: `AnalyticsProjectsPanel`. An absent or loading answer is
 * loading and a refused or failed one is refused. `salespersonMemberIds`,
 * `teamIds` and `stageLists` are JSON text cells read with `JSON.parse`, and
 * `updatedAt` a SQL timestamp; any malformed cell fails the whole model with
 * a message rather than throwing, so no row is drawn half-read. A null name
 * is kept: the row then shows its id as a refused name.
 */
import { DateTime } from 'luxon';
import type { Snapshot } from 'valtio';

import {
  ANALYTICS_OPEN_PROJECT_STAGES,
  ANALYTICS_PROJECT_STAGES,
  analyticsDecimalFraction,
} from '#types';
import type { NamedQueryCell } from '#types';

import { analyticsJsonArray } from './analyticsJsonArray';
import { analyticsMalformedAnswer } from './analyticsMalformedAnswer';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsOpenProjectStage,
  AnalyticsProjectRow,
  AnalyticsProjects,
} from './analyticsProjectsTab.types';
import type { AnalyticsAnswer } from './index.types';

const isString = (item: unknown): item is string => typeof item === 'string';

const openStages = (
  cell: NamedQueryCell,
): readonly AnalyticsOpenProjectStage[] | undefined => {
  const names = analyticsJsonArray(cell, isString);
  const stages = names?.flatMap((name) =>
    ANALYTICS_OPEN_PROJECT_STAGES.filter((stage) => stage === name),
  );
  return stages?.length === names?.length ? stages : undefined;
};

const isDecimalOrNull = (cell: NamedQueryCell): cell is string | null =>
  cell === null ||
  (typeof cell === 'string' && analyticsDecimalFraction(cell) !== undefined);

const isTimestampOrNull = (cell: NamedQueryCell): cell is string | null =>
  cell === null || (typeof cell === 'string' && DateTime.fromSQL(cell).isValid);

const projectRow = (
  row: Snapshot<Record<string, NamedQueryCell>>,
): AnalyticsProjectRow | undefined => {
  const { projectId, projectName, value, updatedAt, totalProjects } = row;
  const stage = ANALYTICS_PROJECT_STAGES.find((s) => s === row.stage);
  const salespersonMemberIds = analyticsJsonArray(
    row.salespersonMemberIds,
    isString,
  );
  const teamIds = analyticsJsonArray(row.teamIds, isString);
  const stageLists = openStages(row.stageLists);
  const valid =
    typeof projectId === 'string' &&
    (projectName === null || typeof projectName === 'string') &&
    stage !== undefined &&
    salespersonMemberIds !== undefined &&
    teamIds !== undefined &&
    stageLists !== undefined &&
    Number.isInteger(totalProjects) &&
    isDecimalOrNull(value) &&
    isTimestampOrNull(updatedAt);
  return valid
    ? {
        projectId,
        projectName,
        stage,
        salespersonMemberIds,
        teamIds,
        stageLists,
        value,
        updatedAt,
      }
    : undefined;
};

export const analyticsProjects = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
): AnalyticsProjects => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  const { result } = answer;
  const rows = result.rows.map(projectRow);
  const parsed = rows.filter(
    (row): row is AnalyticsProjectRow => row !== undefined,
  );
  if (parsed.length !== rows.length)
    return analyticsMalformedAnswer('projectsByStage');
  const [first] = result.rows;
  const totalProjects = first?.totalProjects ?? 0;
  const totalValue = first === undefined ? '0' : first.totalValue;
  if (
    typeof totalProjects !== 'number' ||
    (totalValue !== null &&
      (typeof totalValue !== 'string' ||
        analyticsDecimalFraction(totalValue) === undefined))
  )
    return analyticsMalformedAnswer('projectsByStage');
  return {
    status: 'answered',
    rows: parsed,
    totalProjects,
    totalValue,
    refusedValue: analyticsRefusedColumn(result, 'value'),
  };
};
